import React, { useEffect, useState } from 'react';
import { Agente } from '../types';
import api from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import { formatTime24 } from '../utils/dateTime';

interface AsisDetalleMeta {
  userId: string;
  name: string;
  idCard: string;
  hospital: string;
  periodoDesde: string;
  periodoHasta: string;
  servidor: string;
  horario: string;
  ld: string;
}

interface AsisDetalleRow {
  userid: string;
  idcard: string;
  name: string;
  fecha: string;
  horaIngreso: string;
  horaEgreso: string;
  novedad: string;
  novedadDesde: string;
  novedadHasta: string;
  diaSemana: string;
  minutosTrabajados: number | null;
}

interface AsisDetalleResponse {
  meta: AsisDetalleMeta;
  rows: AsisDetalleRow[];
}

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayIso = (): string => toIsoDate(new Date());

const getCurrentWeekStartIso = (): string => {
  const now = new Date();
  const mondayOffset = (now.getDay() + 6) % 7;
  const monday = new Date(now);
  monday.setDate(now.getDate() - mondayOffset);
  return toIsoDate(monday);
};

const ReportesPage: React.FC = () => {
  const [agentes, setAgentes] = useState<Agente[]>([]);
  const [agenteId, setAgenteId] = useState('');
  const [desde, setDesde] = useState<string>(() => getCurrentWeekStartIso());
  const [hasta, setHasta] = useState<string>(() => getTodayIso());
  const [data, setData] = useState<AsisDetalleResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<Agente[]>('/reports/agents')
      .then((a) => {
        const rows = Array.isArray(a.data) ? a.data : [];
        console.info('[reportes] agentes cargados para dropdown', {
          total: rows.length,
        });
        setAgentes(rows);
      })
      .catch((err) => {
        console.error('[reportes] error cargando agentes para dropdown', err);
        setAgentes([]);
      });
  }, []);

  const handleGenerar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setData(null);
    setLoading(true);
    try {
      if (!agenteId) {
        setError('Seleccione un agente.');
        setLoading(false);
        return;
      }
      if (!desde || !hasta) {
        setError('Seleccione un periodo desde y hasta.');
        setLoading(false);
        return;
      }

      const res = await api.get<AsisDetalleResponse>(`/reports/asis/${agenteId}`, {
        params: { desde, hasta },
      });
      setData(res.data);
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      if (!agenteId) return;
      const res = await api.get(`/reports/asis/${agenteId}/csv`, {
        params: { desde, hasta },
        responseType: 'blob',
      });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = href;
      a.download = `reporte-asis-detalle-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(href);
    } catch {}
  };

  return (
    <>
      <h1 className="page-title">Geo Reportes</h1>

      <div className="card">
        <h2 className="card-title">Reporte Asis - Detalle</h2>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={handleGenerar}>
          <div className="form-grid">
            <div className="form-group">
              <label>Agente</label>
              <select value={agenteId} onChange={e => setAgenteId(e.target.value)}>
                <option value="">Seleccione...</option>
                {agentes.map(a => (
                  <option key={a._id} value={a._id}>{a.nombre}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Desde</label>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Hasta</label>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Generando...' : 'Generar reporte'}
            </button>
            {data && (
              <button type="button" className="btn btn-outline" onClick={handleExportCSV}>
                Exportar Excel
              </button>
            )}
          </div>
        </form>
      </div>

      {data && (
        <div className="card">
          <div className="form-grid" style={{ marginBottom: '16px' }}>
            <div><strong>UserId:</strong> {data.meta.userId}</div>
            <div><strong>Name:</strong> {data.meta.name}</div>
            <div><strong>IDCard:</strong> {data.meta.idCard}</div>
            <div><strong>Hospital:</strong> {data.meta.hospital}</div>
            <div><strong>Periodo:</strong> {data.meta.periodoDesde} a {data.meta.periodoHasta}</div>
            <div><strong>Servidor:</strong> {data.meta.servidor}</div>
            <div><strong>Horario:</strong> {data.meta.horario || '-'}</div>
            <div><strong>LD:</strong> {data.meta.ld}</div>
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
              
                  <th>fecha</th>
                  <th>hora ingreso</th>
                  <th>hora egreso</th>
                  <th>Tipo-Registro</th>
            
                  <th>dia Semana</th>
                  <th>Minutos trabajados</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr key={`${row.fecha}-${i}`}>
                  
                    <td>{row.fecha}</td>
                    <td>{row.horaIngreso ? formatTime24(row.horaIngreso) : '-'}</td>
                    <td>{row.horaEgreso ? formatTime24(row.horaEgreso) : '-'}</td>
                    <td>{row.novedad || '-'}</td>
                   
                    <td>{row.diaSemana}</td>
                    <td>{row.minutosTrabajados ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportesPage;
